import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { AgendaItem } from '../store/schema/AgendaItem';
import { DateRange } from 'owa-datetime-utils';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { getShowDeclinedMeetingsEnabled } from './userConfigSelectors';
import {
    getLockedCalendarEventsReader,
    getLockedCalendarEventsStore,
    EventsCacheLockId,
    LockedCalendarEventsReader,
} from 'owa-calendar-events-store';
import { compareCalendarEvents } from 'owa-calendar-types';
import { now, DisplayDate, startOfDay, endOfDay, addMilliseconds } from 'owa-datetime';
import { isBefore, differenceInMinutes } from 'owa-datetime';
import { canRead, isNewCalendarItem } from 'owa-calendar-event-capabilities';

interface AgendaLockedCalendarEventsReader extends LockedCalendarEventsReader {
    getOrderedEvents: (folderId: string[], dateRange: DateRange) => CalendarEvent[];
    getCalendarAgendaItems: (folderId: string[], dateRange: DateRange) => AgendaItem[];
    getUpNextEvent: (folderId: string[]) => { event: CalendarEvent; index: number } | null;
}

export function getAgendaLockedCalendarEventsReader(
    lockId: EventsCacheLockId
): AgendaLockedCalendarEventsReader {
    const lockedReader = getLockedCalendarEventsReader(lockId);
    return {
        ...lockedReader,
        getOrderedEvents: (folderId: string[], dateRange: DateRange) =>
            getOrderedEvents(folderId, dateRange, lockId),
        getCalendarAgendaItems: (folderId: string[], dateRange: DateRange) =>
            getOrderedEvents(folderId, dateRange, lockId).map(getAgendaItem),
        getUpNextEvent: (folderId: string[]) => getUpNextEvent(folderId, lockId),
    };
}

export { getLockedCalendarEventsStore };

function getOrderedEvents(
    folderId: string[],
    dateRange: DateRange,
    lockId: EventsCacheLockId
): CalendarEvent[] {
    const getCalendarEventsForDateRange = getLockedCalendarEventsReader(lockId)
        .getCalendarEventsForDateRange;
    let events: CalendarEvent[] = [];
    folderId.forEach(folder => {
        events.push(...getCalendarEventsForDateRange(folder, dateRange));
    });

    if (!getShowDeclinedMeetingsEnabled()) {
        events = events.filter(event => event.ResponseType !== 'Decline');
    }

    return events.sort(compareCalendarEvents);
}

function getAgendaItem(calendarEvent: CalendarEvent): AgendaItem {
    return {
        id: calendarEvent.ItemId,
        subject: calendarEvent.Subject,
        startTime: calendarEvent.Start,
        endTime: calendarEvent.End,
        location: calendarEvent.Location && calendarEvent.Location.DisplayName,
        organizerName: calendarEvent.Organizer && calendarEvent.Organizer.Mailbox.Name,
        organizerEmail: calendarEvent.Organizer && calendarEvent.Organizer.Mailbox.EmailAddress,
        isAllDay: calendarEvent.IsAllDayEvent,
        isCancelled: calendarEvent.IsCancelled || calendarEvent.ResponseType === 'Decline',
        freeBusyType: calendarEvent.FreeBusyType,
        parentFolderId: calendarEvent.ParentFolderId,
        categories: calendarEvent.Categories,
        isRecurring: calendarEvent.CalendarItemType === 'Occurrence',
        hasAttachments: calendarEvent.HasAttachments,
        isRecurringException: calendarEvent.CalendarItemType === 'Exception',
        isPrivate: calendarEvent.Sensitivity === 'Private',
        isMeeting: isEventAMeeting(calendarEvent),
        joinUrl: calendarEvent.OnlineMeetingJoinUrl,
        provider: calendarEvent.OnlineMeetingProvider,
        charmId: calendarEvent.CharmId,
    };
}

export function getUpNextEvent(
    folderIds: string[],
    lockId: EventsCacheLockId
): { event: CalendarEvent; index: number } | null {
    const { getOrderedEvents } = getAgendaLockedCalendarEventsReader(lockId);

    const currentTime = now();
    const start = addMilliseconds(startOfDay(currentTime), 1);
    const todayDateRange = { start: start, end: endOfDay(currentTime) };
    const events = getOrderedEvents(folderIds, todayDateRange);
    const upNextEvents = events.filter(isUpNext(currentTime));
    const upNextEvent = upNextEvents.length > 0 ? upNextEvents[0] : null;
    return upNextEvent ? { event: upNextEvent, index: events.indexOf(upNextEvent) } : null;
}

/** Returns true if the event is in the future */
function isUpNext(currentTime: DisplayDate): (event: CalendarEvent) => boolean {
    return (event: CalendarEvent): boolean => {
        const eventIsInThePast = isBefore(event.End, currentTime);
        const eventStartedMoreThanFiveMinsAgo = differenceInMinutes(currentTime, event.Start) > 5;
        /** if false, this calendar item has not been created on the server, so it cannot be the upnext event */
        const eventExistsOnServer = !isNewCalendarItem(event);
        /** if false, this calendar item is a draft or only exposes free/busy status (no full details), so it cannot be the upnext event */
        const canReadEvent = canRead(event);
        return (
            !eventIsInThePast &&
            !event.IsAllDayEvent &&
            !event.IsCancelled &&
            !eventStartedMoreThanFiveMinsAgo &&
            eventExistsOnServer &&
            canReadEvent
        );
    };
}
